import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import OneOmicsLogo from "./OneOmicsLogo";

export default function LoginPage({ onGoRegister }) {
  const [form, setForm]             = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]           = useState("");
  const [errorType, setErrorType]   = useState("error"); // "error" | "pending" | "rejected"
  const [loading, setLoading]       = useState(false);
  const [attempts, setAttempts]     = useState(0);
  const [lockout, setLockout]       = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(0);
  const [mounted, setMounted]       = useState(false);
  const { login }                   = useAuth();

  useEffect(() => {
    setMounted(true);
    const savedLockout = localStorage.getItem("lockoutUntil");
    if (savedLockout && Date.now() < parseInt(savedLockout)) {
      setLockout(true);
      startLockoutCountdown(parseInt(savedLockout));
    }
  }, []);

  const startLockoutCountdown = (until) => {
    const interval = setInterval(() => {
      const remaining = Math.ceil((until - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockout(false); setAttempts(0); setLockoutTimer(0);
        localStorage.removeItem("lockoutUntil"); clearInterval(interval);
      } else { setLockoutTimer(remaining); }
    }, 1000);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockout) return;
    if (!form.username.trim() || !form.password.trim()) {
      setError("All fields are required."); setErrorType("error"); return;
    }
    setLoading(true); setError("");
    try {
      const result = await login(form.username.trim(), form.password);
      if (result.success) {
        // AppShell will auto-switch to dashboard
      } else {
        // Show pending / rejected as a special styled alert
        if (result.status === "pending") {
          setErrorType("pending");
          setError("Account pending admin approval. Please wait.");
        } else if (result.status === "rejected") {
          setErrorType("rejected");
          setError("Your account request was rejected. Contact admin.");
        } else {
          setErrorType("error");
          const newAttempts = attempts + 1;
          setAttempts(newAttempts);
          if (newAttempts >= 5) {
            const lockoutUntil = Date.now() + 5 * 60 * 1000;
            localStorage.setItem("lockoutUntil", lockoutUntil.toString());
            setLockout(true); startLockoutCountdown(lockoutUntil);
            setError("Too many failed attempts. Locked for 5 minutes.");
          } else {
            setError(result.message || `Invalid credentials. ${5 - newAttempts} attempts remaining.`);
          }
        }
      }
    } catch { setError("Server error. Please try again."); setErrorType("error"); }
    finally { setLoading(false); }
  };

  const alertClass = errorType === "pending" ? "alert-pending"
    : errorType === "rejected" ? "alert-rejected"
    : "alert-error";

  const alertIcon = errorType === "pending" ? "⏳"
    : errorType === "rejected" ? "🚫"
    : "⚠";

  return (
    <div className="login-root">
      <div className="grid-overlay" />
      <div className="glow-orb orb-1" />
      <div className="glow-orb orb-2" />

      <div className={`login-container ${mounted ? "mounted" : ""}`}>
        {/* Brand */}
        <div className="brand-header" style={{ marginBottom: 20 }}>
          <OneOmicsLogo height={40} maxWidth="190px" dark={false} />
        </div>

        <div className="divider-line" />

        <div className="login-title-block">
          <h1 className="login-title">Secure Access</h1>
          <p className="login-subtitle">Authorized personnel only</p>
        </div>

        {lockout && (
          <div className="alert alert-lockout">
            <span className="alert-icon">⛔</span>
            <div>
              <div className="alert-title">Account Locked</div>
              <div className="alert-msg">Too many failed attempts. Try again in <strong>{lockoutTimer}s</strong></div>
            </div>
          </div>
        )}

        {error && !lockout && (
          <div className={`alert ${alertClass}`}>
            <span className="alert-icon">{alertIcon}</span>
            <div>
              {errorType === "pending" && <div className="alert-title">Awaiting Approval</div>}
              {errorType === "rejected" && <div className="alert-title">Access Denied</div>}
              <div className="alert-msg">{error}</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
          <div className="field-group">
            <label className="field-label">USERNAME</label>
            <div className="field-wrapper">
              <span className="field-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              <input type="text" name="username" value={form.username} onChange={handleChange}
                placeholder="Enter admin username" disabled={lockout || loading} className="field-input"
                autoComplete="off" spellCheck="false" />
            </div>
          </div>

          <div className="field-group">
            <label className="field-label">PASSWORD</label>
            <div className="field-wrapper">
              <span className="field-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </span>
              <input type={showPassword ? "text" : "password"} name="password" value={form.password}
                onChange={handleChange} placeholder="Enter your password"
                disabled={lockout || loading} className="field-input" autoComplete="new-password" />
              <button type="button" className="toggle-pass" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword
                  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
          </div>

          {attempts > 0 && (
            <div className="attempt-bar">
              {[...Array(5)].map((_, i) => (
                <div key={i} className={`attempt-dot ${i < attempts ? "used" : ""}`} />
              ))}
              <span className="attempt-label">{attempts}/5 attempts</span>
            </div>
          )}

          <button type="submit" disabled={loading || lockout} className={`submit-btn ${loading ? "loading" : ""}`}>
            {loading ? (
              <span className="btn-loading"><span className="spinner" />AUTHENTICATING...</span>
            ) : lockout ? `LOCKED · ${lockoutTimer}s` : (
              <><span>SIGN IN</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          <div className="security-badges">
            <span className="badge">🔒 TLS Encrypted</span>
            <span className="badge">🛡 JWT Secured</span>
          </div>
          {onGoRegister && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              Don't have an account?{" "}
              <button onClick={onGoRegister} style={{
                background: "none", border: "none", color: "#00b4a0",
                cursor: "pointer", fontFamily: "'Barlow', sans-serif",
                fontSize: 13, textDecoration: "underline", textUnderlineOffset: 3,
              }}>Request access</button>
            </p>
          )}
          <p className="footer-note">This system is monitored. Unauthorized access is prohibited.</p>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        .login-root{min-height:100vh;background:#f0f4f8;display:flex;align-items:center;justify-content:center;font-family:'Barlow', sans-serif;position:relative;overflow:hidden}
        .grid-overlay{display:none}
        .glow-orb{display:none}
        .login-container{width:100%;max-width:440px;background:#ffffff;border:1px solid #e2e8f0;border-radius:16px;padding:40px 36px;position:relative;z-index:10;box-shadow:0 4px 24px rgba(0,0,0,0.08),0 1px 4px rgba(0,0,0,0.04);opacity:0;transform:translateY(24px);transition:opacity 0.5s ease,transform 0.5s ease}
        .login-container.mounted{opacity:1;transform:translateY(0)}
        .brand-header{display:flex;align-items:center;gap:14px;margin-bottom:24px}
        .brand-icon{color:#00b4a0;width:36px;flex-shrink:0}.brand-icon svg{width:100%;height:auto}
        .brand-text{display:flex;flex-direction:column}
        .brand-name{font-family:'Barlow', sans-serif;font-size:15px;font-weight:700;letter-spacing:0.18em;color:#1a2a3a}
        .brand-sub{font-family:'Barlow', sans-serif;font-size:9px;letter-spacing:0.22em;color:#00b4a0;margin-top:1px}
        .divider-line{height:1px;background:linear-gradient(90deg,transparent,rgba(0,180,160,0.3),transparent);margin-bottom:28px}
        .login-title-block{margin-bottom:28px}
        .login-title{font-size:28px;font-weight:700;color:#1a2a3a;letter-spacing:-0.02em;line-height:1.1}
        .login-subtitle{font-size:13px;color:#64748b;margin-top:5px;letter-spacing:0.01em}
        .alert{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;border-radius:10px;margin-bottom:20px;font-size:13px;animation:alertIn 0.3s ease}
        @keyframes alertIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        .alert-error{background:rgba(239,68,68,0.07);border:1px solid rgba(239,68,68,0.2);color:#dc2626}
        .alert-lockout{background:rgba(245,158,11,0.07);border:1px solid rgba(245,158,11,0.2);color:#d97706}
        .alert-pending{background:rgba(59,130,246,0.07);border:1px solid rgba(59,130,246,0.2);color:#2563eb}
        .alert-rejected{background:rgba(239,68,68,0.07);border:1px solid rgba(239,68,68,0.2);color:#dc2626}
        .alert-title{font-weight:700;font-size:13px;margin-bottom:2px}
        .alert-msg{font-size:12px;opacity:0.9}
        .alert-icon{font-size:16px;margin-top:1px}
        .login-form{display:flex;flex-direction:column;gap:18px}
        .field-group{display:flex;flex-direction:column;gap:6px}
        .field-label{font-family:'Barlow', sans-serif;font-size:11px;font-weight:500;letter-spacing:0.1em;color:#64748b;text-transform:uppercase}
        .field-wrapper{position:relative;display:flex;align-items:center}
        .field-icon{position:absolute;left:14px;color:#94a3b8;display:flex;align-items:center;pointer-events:none;transition:color 0.2s}
        .field-wrapper:focus-within .field-icon{color:#00b4a0}
        .field-input{width:100%;background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:10px;padding:13px 14px 13px 44px;color:#1a2a3a;font-family:'Barlow', sans-serif;font-size:14px;outline:none;transition:all 0.2s}
        .field-input::placeholder{color:#cbd5e1}
        .field-input:focus{border-color:#00b4a0;background:#fff;box-shadow:0 0 0 3px rgba(0,180,160,0.1)}
        .field-input:disabled{opacity:0.5;cursor:not-allowed;background:#f1f5f9}
        .toggle-pass{position:absolute;right:14px;background:none;border:none;color:#94a3b8;cursor:pointer;padding:4px;display:flex;align-items:center;transition:color 0.2s}
        .toggle-pass:hover{color:#64748b}
        .attempt-bar{display:flex;align-items:center;gap:6px}
        .attempt-dot{width:8px;height:8px;border-radius:50%;background:#e2e8f0;transition:background 0.3s}
        .attempt-dot.used{background:#ef4444;box-shadow:0 0 6px rgba(239,68,68,0.3)}
        .attempt-label{font-family:'Barlow', sans-serif;font-size:11px;color:#94a3b8;margin-left:4px}
        .submit-btn{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;padding:14px;background:linear-gradient(135deg,#00c49a 0%,#00a882 100%);color:#fff;border:none;border-radius:10px;font-family:'Barlow', sans-serif;font-size:14px;font-weight:700;letter-spacing:0.08em;cursor:pointer;transition:all 0.2s;margin-top:4px;box-shadow:0 4px 16px rgba(0,180,160,0.3)}
        .submit-btn:hover:not(:disabled){background:linear-gradient(135deg,#00d4a8 0%,#00b88e 100%);box-shadow:0 6px 24px rgba(0,180,160,0.4);transform:translateY(-1px)}
        .submit-btn:disabled{opacity:0.5;cursor:not-allowed;box-shadow:none}
        .btn-loading{display:flex;align-items:center;gap:10px}
        .spinner{width:14px;height:14px;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:spin 0.7s linear infinite}
        @keyframes spin{to{transform:rotate(360deg)}}
        .login-footer{margin-top:28px;display:flex;flex-direction:column;align-items:center;gap:10px}
        .security-badges{display:flex;gap:10px}
        .badge{font-family:'Barlow', sans-serif;font-size:10px;color:#64748b;background:#f1f5f9;border:1px solid #e2e8f0;padding:4px 10px;border-radius:999px;letter-spacing:0.03em}
        .footer-note{font-size:11px;color:#94a3b8;text-align:center}
        @media(max-width:480px){.login-container{margin:16px;padding:28px 22px}.login-title{font-size:24px}}
      `}</style>
    </div>
  );
}